import React, { useCallback, useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { noticiasApi } from '../services/api';

const LIMIT = 20;

// Colores por fuente (hex exactos del diseño).
const FUENTE = {
  iagua: { label: 'iAgua', color: '#F97316', bg: '#FFF7ED' },
  tecnoaqua: { label: 'TecnoAqua', color: '#0284C7', bg: '#EFF6FF' },
  retema: { label: 'RETEMA', color: '#16A34A', bg: '#F0FDF4' },
};
const fuenteMeta = (f) => FUENTE[f] || { label: f || '—', color: '#6B7280', bg: '#F9FAFB' };

const RELEVANCIA = {
  A: { label: 'Oportunidad', cls: 'bg-orange-600 text-white' },
  B: { label: 'Mercado', cls: 'bg-blue-600 text-white' },
  C: { label: 'Contexto', cls: 'bg-gray-400 text-white' },
};
const relMeta = (r) => RELEVANCIA[r] || RELEVANCIA.C;

const FILTROS = [
  { key: '', label: 'Todas' },
  { key: 'A', label: 'Oportunidades A' },
  { key: 'B', label: 'Mercado B' },
  { key: 'C', label: 'Contexto C' },
];

const clamp = (n) => ({
  display: '-webkit-box',
  WebkitLineClamp: n,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

function haceCuanto(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  const seg = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seg < 60) return 'hace un momento';
  const min = Math.floor(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias < 30) return `hace ${dias} d`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Badges({ noticia, sobreImagen = false }) {
  const f = fuenteMeta(noticia.fuente);
  const rel = relMeta(noticia.relevancia);
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="text-[11px] font-semibold px-2 py-0.5 rounded"
        style={sobreImagen
          ? { background: 'rgba(255,255,255,0.9)', color: f.color }
          : { background: f.bg, color: f.color }}
      >
        {f.label}
      </span>
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${rel.cls}`}>{rel.label}</span>
    </div>
  );
}

function VoteButtons({ noticia, onValorar }) {
  const voto = noticia.mi_valoracion;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onValorar(noticia, 1)}
        aria-pressed={voto === 1}
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
          voto === 1 ? 'bg-green-600 text-white border-green-600' : 'text-gray-500 border-gray-300 hover:bg-gray-50'
        }`}
      >
        <ThumbsUp className="w-3.5 h-3.5" /> Útil
      </button>
      <button
        type="button"
        onClick={() => onValorar(noticia, -1)}
        aria-pressed={voto === -1}
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
          voto === -1 ? 'bg-red-600 text-white border-red-600' : 'text-gray-500 border-gray-300 hover:bg-gray-50'
        }`}
      >
        <ThumbsDown className="w-3.5 h-3.5" /> No relevante
      </button>
    </div>
  );
}

function FeaturedCard({ noticia, onValorar }) {
  const [imgError, setImgError] = useState(false);
  const f = fuenteMeta(noticia.fuente);
  const hasImage = noticia.imagen_url && !imgError;
  const fecha = haceCuanto(noticia.fecha_publicacion || noticia.fecha_ingesta);

  if (hasImage) {
    return (
      <article className="relative rounded-xl overflow-hidden shadow-sm border border-gray-200 mb-5">
        <img
          src={noticia.imagen_url}
          alt=""
          onError={() => setImgError(true)}
          className="w-full object-cover"
          style={{ height: '280px' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <Badges noticia={noticia} sobreImagen />
          <a
            href={noticia.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-2 text-2xl font-bold text-white leading-tight hover:underline"
          >
            {noticia.titulo}
          </a>
          {noticia.resumen && (
            <p className="mt-2 text-base text-gray-100/90 leading-relaxed" style={clamp(3)}>{noticia.resumen}</p>
          )}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-gray-300">{fecha}</span>
            <VoteButtons noticia={noticia} onValorar={onValorar} />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className="rounded-xl overflow-hidden shadow-sm mb-5 p-5"
      style={{ background: f.bg, borderLeft: `6px solid ${f.color}` }}
    >
      <Badges noticia={noticia} />
      <a
        href={noticia.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block mt-2 text-2xl font-bold text-gray-900 leading-tight hover:underline"
      >
        {noticia.titulo}
      </a>
      {noticia.resumen && (
        <p className="mt-2 text-base text-gray-600 leading-relaxed" style={clamp(3)}>{noticia.resumen}</p>
      )}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-400">{fecha}</span>
        <VoteButtons noticia={noticia} onValorar={onValorar} />
      </div>
    </article>
  );
}

function NewsCard({ noticia, onValorar }) {
  const [imgError, setImgError] = useState(false);
  const f = fuenteMeta(noticia.fuente);
  const hasImage = noticia.imagen_url && !imgError;
  const fecha = haceCuanto(noticia.fecha_publicacion || noticia.fecha_ingesta);

  return (
    <article
      className="rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white flex flex-col"
      style={hasImage ? undefined : { background: f.bg, borderLeft: `4px solid ${f.color}` }}
    >
      {hasImage && (
        <img
          src={noticia.imagen_url}
          alt=""
          onError={() => setImgError(true)}
          className="w-full object-cover"
          style={{ height: '180px' }}
        />
      )}
      <div className="p-4 flex flex-col flex-1">
        <Badges noticia={noticia} />
        <a
          href={noticia.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-2 text-base font-bold text-gray-900 leading-snug hover:underline"
          style={clamp(2)}
        >
          {noticia.titulo}
        </a>
        {noticia.resumen && (
          <p className="mt-1.5 text-sm text-gray-500 leading-relaxed" style={clamp(3)}>{noticia.resumen}</p>
        )}
        <div className="mt-3 pt-2 flex items-center justify-between">
          <span className="text-xs text-gray-400">{fecha}</span>
          <VoteButtons noticia={noticia} onValorar={onValorar} />
        </div>
      </div>
    </article>
  );
}

export default function NoticiasSector() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [relevancia, setRelevancia] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await noticiasApi.list({ page, limit: LIMIT, relevancia: relevancia || undefined });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || 'Error cargando noticias');
    } finally {
      setLoading(false);
    }
  }, [page, relevancia]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleValorar = async (noticia, valoracion) => {
    const previo = noticia.mi_valoracion;
    setItems((prev) => prev.map((n) => (n.id === noticia.id ? { ...n, mi_valoracion: valoracion } : n)));
    try {
      await noticiasApi.valorar(noticia.id, valoracion);
    } catch (e) {
      setItems((prev) => prev.map((n) => (n.id === noticia.id ? { ...n, mi_valoracion: previo } : n)));
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / LIMIT));
  const destacada = items[0];
  const resto = items.slice(1);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <header className="mb-5 border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Noticias del Sector Agua</h1>
        <p className="mt-1 text-sm text-gray-500">iAgua · TecnoAqua · RETEMA</p>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {FILTROS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              onClick={() => { setRelevancia(f.key); setPage(1); }}
              className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${
                relevancia === f.key
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-sm text-gray-400">{total} noticias</span>
        </div>
      </header>

      {loading && <p className="text-sm text-gray-400 py-10 text-center">Cargando…</p>}
      {error && <p className="text-sm text-red-600 py-6 text-center">{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-gray-400 py-10 text-center">No hay noticias para este filtro.</p>
      )}

      {!loading && !error && destacada && (
        <>
          <FeaturedCard noticia={destacada} onValorar={handleValorar} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resto.map((n) => <NewsCard key={n.id} noticia={n} onValorar={handleValorar} />)}
          </div>
        </>
      )}

      {totalPaginas > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
          >
            ← Anterior
          </button>
          <span className="text-sm text-gray-500">Página {page} de {totalPaginas}</span>
          <button
            type="button"
            disabled={page >= totalPaginas}
            onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}
